import { useState } from 'react';
import { GitBranch, Plus, Copy, Trash2, Pencil, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

export function ScenarioSelector({
  scenarios,
  activeScenarioId,
  onActivate,
  onCreate,
  onDuplicate,
  onDelete,
  onRename,
  loading,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const hasScenarios = scenarios && scenarios.length > 0;
  const activeScenario = hasScenarios
    ? scenarios.find(s => s.id === activeScenarioId) || null
    : null;

  const handleNewScenario = () => {
    const nextNum = (scenarios?.length || 0) + 1;
    onCreate(`V${nextNum}`);
  };

  const handleDuplicate = () => {
    if (!activeScenarioId) return;
    const nextNum = (scenarios?.length || 0) + 1;
    const baseName = activeScenario?.name || 'V1';
    onDuplicate(activeScenarioId, `${baseName} copy`);
  };

  const handleStartRename = (scenario) => {
    setEditingId(scenario.id);
    setEditName(scenario.name);
  };

  const handleConfirmRename = () => {
    if (editingId && editName.trim()) {
      onRename(editingId, editName.trim());
    }
    setEditingId(null);
    setEditName('');
  };

  const handleCancelRename = () => {
    setEditingId(null);
    setEditName('');
  };

  // No scenarios yet — show a simple "create first scenario" button
  if (!hasScenarios) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleNewScenario}
        disabled={loading}
        className="gap-1.5 h-8 text-xs"
        data-testid="create-first-scenario-btn"
      >
        <GitBranch className="h-3.5 w-3.5" />
        Scenarios
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-1.5" data-testid="scenario-selector">
      <GitBranch className="h-3.5 w-3.5 text-gray-400" />

      {/* Active scenario selector */}
      <Select
        value={activeScenarioId || ''}
        onValueChange={(id) => { if (id !== activeScenarioId) onActivate(id); }}
      >
        <SelectTrigger className="h-7 w-auto min-w-[70px] max-w-[140px] text-xs font-medium border-gray-200 px-2 gap-1" data-testid="scenario-dropdown">
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent>
          {scenarios.map(s => (
            <SelectItem key={s.id} value={s.id} data-testid={`scenario-option-${s.id}`}>
              <span className="truncate">{s.name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Scenario actions */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            disabled={loading}
            className="h-7 w-7 text-gray-400 hover:text-gray-700"
            data-testid="scenario-actions-btn"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={handleNewScenario} data-testid="scenario-new-action">
            <Plus className="h-3.5 w-3.5 mr-2" />
            New Scenario
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDuplicate} disabled={!activeScenarioId} data-testid="scenario-duplicate-action">
            <Copy className="h-3.5 w-3.5 mr-2" />
            Duplicate Current
          </DropdownMenuItem>
          {activeScenario && (
            <DropdownMenuItem onClick={() => handleStartRename(activeScenario)} data-testid="scenario-rename-action">
              <Pencil className="h-3.5 w-3.5 mr-2" />
              Rename
            </DropdownMenuItem>
          )}
          {scenarios.length > 1 && activeScenarioId && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  if (window.confirm(`Delete scenario "${activeScenario?.name}"?`)) {
                    onDelete(activeScenarioId);
                  }
                }}
                className="text-red-600 focus:text-red-600"
                data-testid="scenario-delete-action"
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Delete Current
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Inline rename editor */}
      {editingId && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 bg-black/20" onClick={handleCancelRename}>
          <div className="bg-white rounded-lg shadow-lg p-4 min-w-[260px]" onClick={e => e.stopPropagation()} data-testid="scenario-rename-dialog">
            <p className="text-xs text-gray-500 mb-2">Rename scenario</p>
            <Input
              value={editName}
              onChange={e => setEditName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleConfirmRename(); if (e.key === 'Escape') handleCancelRename(); }}
              className="h-8 text-sm mb-2"
              autoFocus
              data-testid="scenario-rename-input"
            />
            <div className="flex justify-end gap-1.5">
              <Button variant="ghost" size="sm" onClick={handleCancelRename} className="h-7 text-xs gap-1">
                <X className="h-3 w-3" /> Cancel
              </Button>
              <Button size="sm" onClick={handleConfirmRename} className="h-7 text-xs gap-1 bg-gray-900 text-white hover:bg-gray-800" data-testid="scenario-rename-confirm">
                <Check className="h-3 w-3" /> Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
